import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  XCircle
} from 'lucide-react';

const SortButton = ({ field, label, sortField, sortDirection, onSort }) => (
  <button
    onClick={() => onSort(field)}
    className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
  >
    {label}
    {sortField === field ? (
      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
    ) : (
      <ArrowUpDown className="w-3 h-3 opacity-50" />
    )}
  </button>
);

export const StudentsTable = ({
  students,
  sortField,
  sortDirection,
  onSort,
  onAction,
  canEdit,
  canDelete
}) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left">
                <SortButton field="name" label="Student Name" sortField={sortField} sortDirection={sortDirection} onSort={onSort} />
              </th>
              <th className="px-4 py-3 text-left">
                <SortButton field="student_id" label="Student ID" sortField={sortField} sortDirection={sortDirection} onSort={onSort} />
              </th>
              <th className="px-4 py-3 text-left">
                <SortButton field="grade" label="Grade" sortField={sortField} sortDirection={sortDirection} onSort={onSort} />
              </th>
              <th className="px-4 py-3 text-left">
                <SortButton field="parent_email" label="Parent" sortField={sortField} sortDirection={sortDirection} onSort={onSort} />
              </th>
              <th className="px-4 py-3 text-left">Tier</th>
              <th className="px-4 py-3 text-left">Topics</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {students.map((student) => (
              <tr key={student.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{student.name || 'N/A'}</div>
                </td>
                <td className="px-4 py-3">
                  <code className="text-sm bg-slate-100 px-2 py-1 rounded">{student.student_id}</code>
                </td>
                <td className="px-4 py-3">
                  <Badge variant="outline">Grade {student.grade}</Badge>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {student.parent_email || 'N/A'}
                </td>
                <td className="px-4 py-3">
                  <Badge className={
                    student.subscription_tier === 'pro' ? 'bg-purple-100 text-purple-700' :
                    student.subscription_tier === 'plus' ? 'bg-blue-100 text-blue-700' :
                    student.subscription_tier === 'starter' ? 'bg-green-100 text-green-700' :
                    'bg-slate-100 text-slate-700'
                  }>
                    {student.subscription_tier || 'free'}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  {student.topic_preferences_completed ? (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-slate-300" />
                  )}
                </td>
                <td className="px-4 py-3">
                  <Badge className={student.active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                    {student.active ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onAction('view', student)}
                      data-testid={`view-student-${student.student_id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onAction('edit', student)}
                        data-testid={`edit-student-${student.student_id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                    {canEdit && (
                      <Switch
                        checked={student.active}
                        onCheckedChange={() => onAction('toggle', student)}
                        data-testid={`toggle-student-${student.student_id}`}
                      />
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => onAction('delete', student)}
                        data-testid={`delete-student-${student.student_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {students.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                  No students found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StudentsTable;
